import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Grid, Switch, TextField, Button,
  FormControl, InputLabel, Select, MenuItem, Alert, Divider,
  Checkbox, Card, CardContent, CircularProgress,
} from '@mui/material';
import {
  SmartToy as AiIcon,
  Save as SaveIcon,
  CheckCircle as SelectedIcon,
} from '@mui/icons-material';
import aiAssistantService from '../../services/ai/aiAssistant.service';
import ApiService from '../../services/api.service';
import AiProviderKeyCard from '../../components/AiProviderKeyCard';
import { getBrandColor } from '../../utils/colorUtils';
import { can, isPlatformLevel } from '../../utils/roleUtils';

const PROVIDERS = [
  { value: 'ANTHROPIC', label: 'Anthropic (Claude)', desc: 'Uses your Anthropic API key' },
  { value: 'OPENAI', label: 'OpenAI (ChatGPT)', desc: 'Uses your OpenAI API key' },
];

const CATEGORIES = [
  { key: 'operations', label: 'Operations', desc: 'Your business data: work orders, invoices, organizations, wallets, service catalog, activity, and employee document expiry (with Thaklis features)' },
  { key: 'accounting', label: 'Accounting', desc: 'Your cash position, receivables aging and bank-entry matching' },
  { key: 'generic', label: 'Generic', desc: 'Reference only, no customer data: UAE government process guide and Thaklis Pro help' },
];

const errorText = (err) =>
  err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Something went wrong';

// ── Help panel (shared by both views) ────────────────────────────

const KeysHelp = () => (
  <Paper sx={{ p: 3 }}>
    <Typography variant="subtitle1" fontWeight={600} mb={2}>
      About these keys
    </Typography>
    <Typography variant="body2" color="text.secondary" paragraph>
      Each typing center uses <strong>its own</strong> API keys, so usage is billed to your own account.
      The OpenAI key is also used by document extraction (passports, Emirates IDs, visas); the AI
      Assistant uses the key of the provider chosen below.
    </Typography>
    <Divider sx={{ my: 2 }} />
    <Typography variant="body2" color="text.secondary" paragraph>
      <strong>OpenAI key:</strong> platform.openai.com → <em>API keys</em> (starts with <code>sk-</code>).
    </Typography>
    <Typography variant="body2" color="text.secondary" paragraph>
      <strong>Anthropic key:</strong> console.anthropic.com → <em>API keys</em> (starts with <code>sk-ant-</code>).
    </Typography>
    <Typography variant="body2" color="text.secondary">
      <strong>Security:</strong> keys are encrypted at rest and never shown again after saving — only the
      last 4 characters are displayed.
    </Typography>
  </Paper>
);

// ── Platform users: keys of a chosen typing center only ──────────

const PlatformAiKeys = ({ canEdit }) => {
  const [typingCenters, setTypingCenters] = useState([]);
  const [selected, setSelected] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await ApiService.getTypingCenters();
        const list = response.data?.data || [];
        setTypingCenters(list);
        if (list.length > 0) setSelected(list[0].id);
      } catch (err) {
        setError('Failed to load typing centers');
      }
    };
    load();
  }, []);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Alert severity="info" sx={{ mb: 2 }}>
          AI Assistant settings (on/off, model, limits) are managed by each typing center&apos;s admin.
          Here you can manage a typing center&apos;s API keys.
        </Alert>
        {typingCenters.length > 0 && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Select Typing Center</InputLabel>
              <Select value={selected} label="Select Typing Center" onChange={(e) => setSelected(e.target.value)}>
                {typingCenters.map(tc => (
                  <MenuItem key={tc.id} value={tc.id}>{tc.name} ({tc.code})</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Paper>
        )}
        {selected && <AiProviderKeyCard typingCenterId={selected} canEdit={canEdit} />}
      </Grid>
      <Grid item xs={12} md={4}>
        <KeysHelp />
      </Grid>
    </Grid>
  );
};

// ── Typing-center users: keys + AI Assistant settings ────────────

// Before the assistant is first turned on, point it at a provider that actually has a key,
// otherwise enabling it leads straight to "No API key set up".
const providerAndModelFor = (cfg) => {
  const keyFor = (p) => (p === 'ANTHROPIC' ? cfg.has_anthropic_key : cfg.has_openai_key);
  const provider = cfg.preferred_provider || 'ANTHROPIC';
  if (!cfg.enabled && !keyFor(provider)) {
    const withKey = PROVIDERS.map(p => p.value).find(keyFor);
    if (withKey) {
      return { provider: withKey, model: (cfg.models || []).find(m => m.provider === withKey)?.model || '' };
    }
  }
  return { provider, model: cfg.preferred_model || '' };
};

const TcAiSettings = ({ currentUser, canEdit }) => {
  const brandColor = getBrandColor();
  const [config, setConfig] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const [enabled, setEnabled] = useState(false);
  const [provider, setProvider] = useState('ANTHROPIC');
  const [model, setModel] = useState('');
  const [maxTokens, setMaxTokens] = useState('');
  const [retentionDays, setRetentionDays] = useState('');
  const [categories, setCategories] = useState({ operations: true, accounting: true, generic: true });

  const applyConfig = (cfg) => {
    setConfig(cfg);
    setEnabled(!!cfg.enabled);
    const { provider: nextProvider, model: nextModel } = providerAndModelFor(cfg);
    setProvider(nextProvider);
    setModel(nextModel);
    setMaxTokens(cfg.max_tokens_per_day != null ? String(cfg.max_tokens_per_day) : '');
    setRetentionDays(cfg.retention_days != null ? String(cfg.retention_days) : '');
    const enabledCats = cfg.enabled_categories || [];
    setCategories({
      operations: enabledCats.includes('operations'),
      accounting: enabledCats.includes('accounting'),
      generic: enabledCats.includes('generic'),
    });
  };

  const load = useCallback(async () => {
    try {
      applyConfig(await aiAssistantService.getConfig());
      setLoadError(null);
    } catch (err) {
      setLoadError(errorText(err));
    }
  }, []);

  // Key saves only change has_*_key; refresh those without discarding unsaved form edits
  const refreshKeys = useCallback(async () => {
    try {
      const cfg = await aiAssistantService.getConfig();
      setConfig(prev => (prev ? { ...prev, has_anthropic_key: cfg.has_anthropic_key, has_openai_key: cfg.has_openai_key } : cfg));
    } catch (err) {
      setLoadError(errorText(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const modelsFor = (p) => (config?.models || []).filter(m => m.provider === p);

  const handleProviderChange = (next) => {
    if (!canEdit || next === provider) return;
    setProvider(next);
    setModel(modelsFor(next)[0]?.model || '');
  };

  const parseOptionalInt = (value) => (value === '' || value == null ? null : Number(value));

  // The switch saves on its own, using the last saved settings so unsaved form edits
  // are neither sent nor discarded.
  const handleToggleEnabled = async (nextEnabled) => {
    if (!config) return;
    const { provider: savedProvider, model: savedModel } = providerAndModelFor(config);
    if (nextEnabled && !savedModel) {
      setMessage({ type: 'error', text: 'Choose a model under Provider and model, then save, before turning the AI Assistant on.' });
      return;
    }
    setMessage(null);
    setEnabled(nextEnabled);
    setSaving(true);
    try {
      const saved = await aiAssistantService.updateConfig({
        enabled: nextEnabled,
        preferred_provider: savedProvider,
        preferred_model: savedModel,
        max_tokens_per_day: config.max_tokens_per_day ?? null,
        enabled_categories: config.enabled_categories || [],
        retention_days: config.retention_days ?? null,
      });
      setConfig(saved);
      setEnabled(!!saved.enabled);
      setMessage({ type: 'success', text: saved.enabled ? 'AI Assistant turned on.' : 'AI Assistant turned off.' });
    } catch (err) {
      setEnabled(!nextEnabled);
      setMessage({ type: 'error', text: errorText(err) });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    setMessage(null);
    const limit = parseOptionalInt(maxTokens);
    const retention = parseOptionalInt(retentionDays);
    if (limit != null && (!Number.isInteger(limit) || limit <= 0)) {
      setMessage({ type: 'error', text: 'Max tokens per day must be a whole number above 0, or empty for unlimited.' });
      return;
    }
    if (retention != null && (!Number.isInteger(retention) || retention < 1 || retention > 3650)) {
      setMessage({ type: 'error', text: 'Keep sessions for must be 1–3650 days, or empty to keep them forever.' });
      return;
    }
    setSaving(true);
    try {
      const saved = await aiAssistantService.updateConfig({
        enabled,
        preferred_provider: provider,
        preferred_model: model,
        max_tokens_per_day: limit,
        enabled_categories: Object.entries(categories).filter(([, on]) => on).map(([key]) => key),
        retention_days: retention,
      });
      applyConfig(saved);
      setMessage({ type: 'success', text: 'AI Assistant settings saved.' });
    } catch (err) {
      setMessage({ type: 'error', text: errorText(err) });
    } finally {
      setSaving(false);
    }
  };

  const hasKey = provider === 'ANTHROPIC' ? config?.has_anthropic_key : config?.has_openai_key;
  const providerLabel = PROVIDERS.find(p => p.value === provider)?.label || provider;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        {!canEdit && <Alert severity="info" sx={{ mb: 2 }}>You have view-only access to these settings.</Alert>}

        {/* API keys — same tile as the platform Typing Center page; also used by document extraction */}
        <Box sx={{ mb: 3 }}>
          <AiProviderKeyCard typingCenterId={currentUser?.typingCenterId} canEdit={canEdit} onSaved={refreshKeys} />
        </Box>

        <Typography sx={{ fontSize: '1rem', fontWeight: 600, mb: 1.5 }}>AI Assistant</Typography>

        {loadError && (
          <Alert severity="error" sx={{ mb: 2 }} action={<Button size="small" onClick={load}>Retry</Button>}>
            {loadError}
          </Alert>
        )}
        {!config && !loadError && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
        )}

        {config && (
          <>
            {/* Enable */}
            <Paper sx={{ p: 2.5, mb: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>Enable AI Assistant</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    Allow staff with the AI Assistant permission to use the chat
                  </Typography>
                </Box>
                <Switch
                  checked={enabled}
                  onChange={(e) => handleToggleEnabled(e.target.checked)}
                  disabled={!canEdit || saving}
                  color="primary"
                />
              </Box>
              {enabled && !hasKey && (
                <Alert severity="warning" sx={{ mt: 1.5, fontSize: '0.78rem' }}>
                  No {providerLabel} key is saved. Add one above and switch it on, otherwise staff will see
                  &quot;No API key set up&quot;.
                </Alert>
              )}
            </Paper>

            {/* Provider & model */}
            <Paper sx={{ p: 2.5, mb: 2.5 }}>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, mb: 2 }}>Provider and model</Typography>
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                {PROVIDERS.map(p => {
                  const selected = provider === p.value;
                  const keySaved = p.value === 'ANTHROPIC' ? config.has_anthropic_key : config.has_openai_key;
                  return (
                    <Grid item xs={12} sm={6} key={p.value}>
                      <Card
                        variant="outlined"
                        onClick={() => handleProviderChange(p.value)}
                        sx={{
                          height: '100%',
                          borderColor: selected ? brandColor : 'divider',
                          borderWidth: selected ? 2 : 1,
                          bgcolor: selected ? `${brandColor}0D` : 'transparent',
                          cursor: canEdit ? 'pointer' : 'default',
                          opacity: !canEdit && !selected ? 0.6 : 1,
                          transition: 'border-color 0.15s, background-color 0.15s',
                          '&:hover': canEdit && !selected ? { borderColor: `${brandColor}80` } : {},
                        }}
                      >
                        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{p.label}</Typography>
                              <Typography sx={{ fontSize: '0.7rem', color: keySaved ? 'text.secondary' : 'warning.main' }}>
                                {keySaved ? p.desc : 'No key saved'}
                              </Typography>
                            </Box>
                            {selected && <SelectedIcon sx={{ fontSize: 20, color: brandColor }} />}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.85rem' }}>Model</InputLabel>
                <Select
                  value={modelsFor(provider).some(m => m.model === model) ? model : ''}
                  label="Model"
                  onChange={(e) => setModel(e.target.value)}
                  disabled={!canEdit}
                  sx={{ fontSize: '0.85rem' }}
                >
                  {modelsFor(provider).map(m => (
                    <MenuItem key={m.model} value={m.model} sx={{ fontSize: '0.85rem' }}>{m.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Paper>

            {/* Categories */}
            <Paper sx={{ p: 2.5, mb: 2.5 }}>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, mb: 0.5 }}>Enabled tool categories</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 2 }}>
                Staff only get the tools their own permissions allow, within these categories.
              </Typography>
              <Grid container spacing={1.5}>
                {CATEGORIES.map(cat => (
                  <Grid item xs={12} key={cat.key}>
                    <Card
                      variant="outlined"
                      onClick={() => canEdit && setCategories(prev => ({ ...prev, [cat.key]: !prev[cat.key] }))}
                      sx={{
                        borderColor: categories[cat.key] ? brandColor : 'divider',
                        bgcolor: categories[cat.key] ? `${brandColor}08` : 'transparent',
                        cursor: canEdit ? 'pointer' : 'default',
                      }}
                    >
                      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{cat.label}</Typography>
                            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{cat.desc}</Typography>
                          </Box>
                          <Checkbox checked={!!categories[cat.key]} disabled={!canEdit} size="small" color="primary" />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>

            {/* Limits & retention */}
            <Paper sx={{ p: 2.5, mb: 2.5 }}>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, mb: 2 }}>Limits and retention</Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  size="small" label="Max tokens per day" type="number"
                  value={maxTokens} onChange={(e) => setMaxTokens(e.target.value)} disabled={!canEdit}
                  placeholder="Unlimited" helperText="Empty = unlimited (your key, your budget)"
                  sx={{ width: 260, '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
                />
                <TextField
                  size="small" label="Keep chat sessions for (days)" type="number"
                  value={retentionDays} onChange={(e) => setRetentionDays(e.target.value)} disabled={!canEdit}
                  placeholder="Forever" helperText="Empty = keep forever; older chats are deleted nightly"
                  sx={{ width: 260, '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
                />
              </Box>
              {config.usage && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    Today: {(config.usage.tokens_today || 0).toLocaleString()} tokens · {config.usage.sessions_today || 0} sessions
                    {config.usage.percent_used != null && ` · ${config.usage.percent_used}% of the daily limit`}
                  </Typography>
                </Box>
              )}
            </Paper>

            {message && (
              <Alert severity={message.type} sx={{ mb: 2, fontSize: '0.8rem' }} onClose={() => setMessage(null)}>
                {message.text}
              </Alert>
            )}

            {canEdit && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={() => handleSave()}
                  disabled={saving || !model}
                  sx={{ textTransform: 'none' }}
                >
                  {saving ? 'Saving...' : 'Save AI Assistant settings'}
                </Button>
              </Box>
            )}
          </>
        )}
      </Grid>

      <Grid item xs={12} md={4}>
        <KeysHelp />
      </Grid>
    </Grid>
  );
};

// ── Page ─────────────────────────────────────────────────────────

const AiSettings = ({ currentUser }) => {
  const brandColor = getBrandColor();
  const canEdit = can(currentUser, 'settings', 'write');
  const platform = isPlatformLevel(currentUser?.role);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <AiIcon sx={{ color: brandColor }} />
        <Typography sx={{ fontSize: '1.1rem', fontWeight: 600 }}>AI Settings</Typography>
      </Box>
      {platform
        ? <PlatformAiKeys canEdit={canEdit} />
        : <TcAiSettings currentUser={currentUser} canEdit={canEdit} />}
    </Box>
  );
};

export default AiSettings;
